'use client'
import { type RefObject, useEffect, useRef } from 'react'
import type { Swiper as SwiperClass } from 'swiper'

/** Swiper 内部のジェスチャ追跡状態（公開型に含まれないため構造的に定義する） */
type SwiperTouchEventsData = {
  isTouched?: boolean
  isMoved?: boolean
  startMoving?: boolean
  pointerId?: number | null
  touchId?: number | null
}

type SwiperWithTouchEventsData = SwiperClass & { touchEventsData?: SwiperTouchEventsData }

/**
 * pointercancel でジェスチャが中断された Swiper を操作可能な状態に復帰させるフック。
 *
 * Swiper（v12 時点）は onTouchEnd で pointercancel / pointerout / pointerleave /
 * contextmenu を Safari・iOS WebView 以外では処理せず無視する。そのため Chrome
 * （Android 実機・DevTools のスマホエミュレーション等）でドラッグ中にブラウザが
 * ジェスチャを奪って pointercancel を発火すると、内部の touchEventsData
 * （isTouched / isMoved / pointerId）が残留し、以降の pointerdown が pointerId
 * 不一致等のガードですべて拒否されて Swiper が恒久的に操作不能になる。
 * ロック済みシートのダイアログ開閉はレイアウト変化でこの中断を誘発しやすい。
 *
 * document で pointercancel / touchcancel を監視し、追跡状態を手動でリセットして
 * 復帰させる。返り値の ref を Swiper の onSwiper でインスタンスに紐付けること。
 */
export function useSwiperGestureRecovery(): RefObject<SwiperClass | null> {
  const swiperRef = useRef<SwiperClass | null>(null)

  useEffect(() => {
    const reset = (event: Event) => {
      const swiper = swiperRef.current as SwiperWithTouchEventsData | null
      if (!swiper || swiper.destroyed) return
      const data = swiper.touchEventsData
      if (!data) return
      // 追跡中のジェスチャが無ければ何もしない
      if (!data.isTouched && data.pointerId == null && data.touchId == null) return
      // 追跡中とは別ポインタの cancel では何もしない（マルチタッチ時に正常なドラッグを誤リセットしない）
      if (event instanceof PointerEvent && data.pointerId != null && event.pointerId !== data.pointerId) return
      if (
        typeof TouchEvent !== 'undefined' &&
        event instanceof TouchEvent &&
        data.touchId != null &&
        ![...event.changedTouches].some((t) => t.identifier === data.touchId)
      ) {
        return
      }
      const wasMoved = Boolean(data.isTouched && data.isMoved)
      data.isTouched = false
      data.isMoved = false
      data.startMoving = false
      data.pointerId = null
      data.touchId = null
      // ドラッグ途中で中断された場合はスライドが中間位置に残るため現在位置へ戻す
      if (wasMoved) swiper.slideTo(swiper.activeIndex)
    }
    // Swiper 自身のリスナー（bubble）より先に走らせるため capture で登録する
    document.addEventListener('pointercancel', reset, true)
    document.addEventListener('touchcancel', reset, true)
    return () => {
      document.removeEventListener('pointercancel', reset, true)
      document.removeEventListener('touchcancel', reset, true)
    }
  }, [])

  return swiperRef
}
